import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { buildCampaignPath, parseUrlPath } from '../../../../utils/routing';
import './styles.css';

// View type icons
const VIEW_ICONS = {
  flow: ({ size = 12 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3h6v6H3zM15 3h6v6h-6zM9 15h6v6H9z" />
      <path d="M6 9v3h3M18 9v6h-3M12 9v6" />
    </svg>
  ),
  kanban: ({ size = 12 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="5" height="18" rx="1" />
      <rect x="10" y="3" width="5" height="12" rx="1" />
      <rect x="17" y="3" width="5" height="8" rx="1" />
    </svg>
  ),
  table: ({ size = 12 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="3" y1="15" x2="21" y2="15" />
      <line x1="9" y1="3" x2="9" y2="21" />
    </svg>
  ),
  gantt: ({ size = 12 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="2" rx="1" />
      <rect x="3" y="11" width="12" height="2" rx="1" />
      <rect x="3" y="17" width="15" height="2" rx="1" />
    </svg>
  ),
  todolist: ({ size = 12 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
    </svg>
  ),
  neural: ({ size = 12 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7.5 8.5L12 12M12 12l5-4M11.8 12l.2 5.5" />
      <circle cx="7.5" cy="8.5" r="2.2" />
      <circle cx="17" cy="8" r="2.2" />
      <circle cx="12" cy="17.5" r="2.2" />
    </svg>
  ),
};

const ChevronDownIcon = ({ size = 10 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// Default view types for cross-campaign navigation (when we don't have the target campaign's views)
const DEFAULT_VIEW_TYPES = [
  { id: 'flow', name: 'Flow', type: 'flow' },
  { id: 'kanban', name: 'Kanban', type: 'kanban' },
  { id: 'table', name: 'Table', type: 'table' },
  { id: 'gantt', name: 'Gantt', type: 'gantt' },
  { id: 'todolist', name: 'Todo List', type: 'todolist' },
  { id: 'neural', name: 'Astra', type: 'neural' },
];

/**
 * OpenInViewDropdown Component
 * 
 * A dropdown button that shows all available views for navigating 
 * to a mission in a specific view.
 * 
 * Uses URL navigation to navigate to the mission, which enables:
 * - Deep linking: URLs can be shared to open a specific mission in a specific view
 * - Reliable focus: The mission focus is handled by useInitialNavigation after URL change
 * 
 * For nested items from other campaigns:
 * - Pass sourceCampaignId to navigate to the item's actual campaign
 * - When navigating to a different campaign, shows default view types instead of custom views
 * 
 * @param {Object} props
 * @param {string} props.taskId - The task/mission ID
 * @param {string} props.campaignId - The campaign ID (needed for URL navigation)
 * @param {string} props.sourceCampaignId - For nested items: the campaign where this item actually belongs
 * @param {string} props.currentView - Current view type ('flow', 'kanban', etc.)
 * @param {Array} props.customViews - Array of views for this campaign
 * @param {string|null} props.activeCustomViewId - Currently active view ID
 * @param {string} props.variant - 'icon' (just icon button) or 'button' (text + icon)
 * @param {string} props.className - Additional CSS class
 */
const OpenInViewDropdown = ({
  taskId,
  campaignId: campaignIdProp,
  sourceCampaignId = null, // For nested items - navigate to this campaign instead
  currentView = 'flow',
  customViews = [],
  activeCustomViewId = null,
  variant = 'icon',
  className = '',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get current campaign ID from prop or extract from current URL
  const currentCampaignId = useMemo(() => {
    if (campaignIdProp) return campaignIdProp;
    const parsed = parseUrlPath(location.pathname, location.search);
    return parsed.type === 'campaign' ? parsed.id : null;
  }, [campaignIdProp, location.pathname, location.search]);
  
  // For nested items, use sourceCampaignId; otherwise use current campaign
  const targetCampaignId = sourceCampaignId || currentCampaignId;
  
  // Check if this is a cross-campaign navigation (nested item from different campaign)
  const isCrossCampaign = sourceCampaignId && sourceCampaignId !== currentCampaignId;
  
  // When navigating to a different campaign, use default view types
  // (we don't have the target campaign's custom views available)
  const effectiveViews = isCrossCampaign ? DEFAULT_VIEW_TYPES : customViews;
  
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const buttonRef = useRef(null);

  // Determine current view info for display
  const currentViewInfo = useMemo(() => {
    // For cross-campaign, we don't have a "current" view in the target
    if (isCrossCampaign) {
      return { id: null, name: null, type: null };
    }
    if (activeCustomViewId) {
      const view = customViews.find(v => v.id === activeCustomViewId);
      if (view) {
        return { id: view.id, name: view.name, type: view.type };
      }
    }
    // Fall back to first view matching current type, or first view overall
    const matchingView = customViews.find(v => v.type === currentView);
    return matchingView || customViews[0] || { id: 'flow', name: 'Flow', type: 'flow' };
  }, [currentView, activeCustomViewId, customViews, isCrossCampaign]);

  // Use a unique ID for this dropdown instance
  const dropdownId = useRef(`open-in-view-${Math.random().toString(36).substr(2, 9)}`).current;

  const handleToggle = useCallback((e) => {
    e.stopPropagation();
    if (isOpen) {
      setIsOpen(false);
      setMenuPosition(null);
    } else {
      // Dispatch event to close other dropdowns before opening this one
      window.dispatchEvent(new CustomEvent('closeTableDropdowns', { detail: { exceptId: dropdownId } }));
      
      const menuWidth = 200;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const buttonRect = { top: e.clientY, bottom: e.clientY, left: e.clientX, right: e.clientX };

      let top = e.clientY + 4;
      let left = e.clientX;
      let openAbove = false;

      const estimatedMenuHeight = Math.min(effectiveViews.length * 40 + 50, 320);
      if (top + estimatedMenuHeight > viewportHeight && e.clientY > estimatedMenuHeight) {
        openAbove = true;
      }
      if (left + menuWidth > viewportWidth) {
        left = viewportWidth - menuWidth - 8;
      }

      setMenuPosition({ top, left, width: menuWidth, buttonRect, openAbove });
      setIsOpen(true);
    }
  }, [isOpen, dropdownId, effectiveViews.length]);

  const handleSelectView = useCallback((view) => {
    if (!targetCampaignId) {
      console.warn('[OpenInViewDropdown] No campaignId provided, cannot navigate');
      setIsOpen(false);
      setMenuPosition(null);
      return;
    }
    
    // Use URL navigation to navigate to the mission in the selected view.
    // This approach:
    // 1. Updates the URL with the mission ID as a query parameter
    // 2. Triggers useInitialNavigation to handle focusing the mission
    // 3. Enables shareable deep links to specific missions in specific views
    //
    // For cross-campaign navigation (nested items), we navigate to the target campaign
    // using a default view type (not custom view ID, since we don't have those)
    const path = buildCampaignPath(targetCampaignId, view.type, {
      missionId: taskId,
      // Only include customViewId if navigating within the same campaign
      customViewId: isCrossCampaign ? null : view.id,
    });
    
    navigate(path);
    
    setIsOpen(false);
    setMenuPosition(null);
  }, [targetCampaignId, taskId, navigate, isCrossCampaign]);

  const menuRef = useRef(null);

  // Reposition menu when it opens (to measure actual height for above positioning)
  useEffect(() => {
    if (!isOpen || !menuPosition?.openAbove || !menuRef.current) return;
    
    // Measure actual menu height and reposition
    const menuHeight = menuRef.current.getBoundingClientRect().height;
    const newTop = menuPosition.buttonRect.top - menuHeight - 4;
    setMenuPosition(prev => ({ ...prev, top: newTop, openAbove: false })); // Set openAbove to false to prevent re-running
  }, [isOpen, menuPosition?.openAbove]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      // Check if click is inside button or menu
      if (buttonRef.current && buttonRef.current.contains(e.target)) {
        return;
      }
      if (menuRef.current && menuRef.current.contains(e.target)) {
        return;
      }
      setIsOpen(false);
      setMenuPosition(null);
    };
    // Small delay to prevent immediate close
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close on scroll (any ancestor scroll)
  useEffect(() => {
    if (!isOpen) return;
    
    const handleScroll = () => {
      setIsOpen(false);
      setMenuPosition(null);
    };
    
    // Listen on window (captures most scroll events)
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  // Close when another dropdown opens (only listen when this menu is open)
  useEffect(() => {
    if (!isOpen) return;
    
    const handleCloseDropdowns = (e) => {
      if (e.detail?.exceptId !== dropdownId) {
        setIsOpen(false);
        setMenuPosition(null);
      }
    };
    
    window.addEventListener('closeTableDropdowns', handleCloseDropdowns);
    return () => window.removeEventListener('closeTableDropdowns', handleCloseDropdowns);
  }, [isOpen, dropdownId]);

  return (
    <>
      <button
        ref={buttonRef}
        className={`open-in-view-dropdown__trigger ${isOpen ? 'open-in-view-dropdown__trigger--open' : ''} open-in-view-dropdown__trigger--${variant} ${className}`}
        onClick={handleToggle}
        onMouseDown={(e) => e.stopPropagation()}
        title="Open in view..."
      >
        {variant === 'button' ? (
          <>
            <span>Open in…</span>
            <ChevronDownIcon size={10} />
          </>
        ) : (
          /* External link / open icon - suggests navigation to another view */
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
          </svg>
        )}
      </button>

      {isOpen && menuPosition && createPortal(
        <div
          ref={menuRef}
          className="open-in-view-dropdown__menu"
          style={{ top: menuPosition.top, left: menuPosition.left, width: menuPosition.width }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="open-in-view-dropdown__header">
            {isCrossCampaign ? 'Open in campaign…' : 'Open in…'}
          </div>
          <div className="open-in-view-dropdown__section">
            {effectiveViews.map(view => {
              const ViewIcon = VIEW_ICONS[view.type];
              const isCurrent = !isCrossCampaign && currentViewInfo.id === view.id;
              return (
                <button
                  key={view.id}
                  className={`open-in-view-dropdown__item ${isCurrent ? 'open-in-view-dropdown__item--current' : ''}`}
                  onClick={() => handleSelectView(view)}
                >
                  {ViewIcon && <ViewIcon size={12} />}
                  <span>{view.name}</span>
                  {isCurrent && <span className="open-in-view-dropdown__badge">current</span>}
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default OpenInViewDropdown;
